// Phase 4c.7 — shared cell/style helpers for the V-shape panels.
//
// A resolved column is `readonly Cell[]` where `Cell = Scalar |
// Dataset`. Chart/table rendering needs a plain displayable scalar:
// nested `Dataset` cells and `null`/`undefined` collapse to `null`;
// `boolean` renders as its string form. SSOT so Cartesian and Table
// coerce identically.

import type { Color, Stroke } from '#core/lib/expr/ops/dashboard/enums.ts';
import type { Cell } from '#core/lib/expr/value.ts';
import type { MantineColor } from '#core/lib/formatters/colors.ts';

export const cellScalar = (c: Cell | undefined): number | string | null => {
  if (c instanceof Date) {
    return c?.valueOf();
  }
  if (c == null) {
    return null;
  }
  if (typeof c === 'boolean') {
    return String(c);
  }
  return c as number | string;
};

/**
 * Dashboard `Color` → Mantine theme color. The dashboard enum names
 * `purple`/`gray` aren't Mantine theme keys; everything else is the
 * identity. SSOT for every `mantineColorVar(...)` call in the panels.
 */
const COLOR_TO_MANTINE: Record<Color, MantineColor> = {
  blue: 'blue',
  red: 'red',
  green: 'green',
  orange: 'orange',
  purple: 'grape',
  yellow: 'yellow',
  pink: 'pink',
  cyan: 'cyan',
  gray: 'dark',
};

/** Map a dashboard `Color` (or undefined → neutral) to a Mantine color. */
export const mantineColorOf = (color: Color | undefined): MantineColor =>
  color === undefined ? 'dark' : COLOR_TO_MANTINE[color];

/** recharts `strokeDasharray` for a dashboard stroke style. SSOT — used
 * by both series lines and reference markers. */
export const dashArray = (style: Stroke | undefined): string | undefined => {
  if (style === 'dashed') {
    return '6 3';
  }
  if (style === 'dotted') {
    return '2 2';
  }
  return undefined;
};
